import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from '@/components/ui/dialog';
import { 
  UserPlus, Users, ClipboardCheck, MapPin, Building2, BookOpen, Upload, Download,
  Eye, EyeOff, Trash2, RefreshCw, CheckCircle, XCircle, Edit, Mail, History,
  Shield, Search, FileText, AlertCircle, Key
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_CONFIG = {
  candidate: { label: 'Candidate', icon: Users, color: 'bg-slate-100 text-slate-700' },
  examiner: { label: 'Examiner', icon: ClipboardCheck, color: 'bg-blue-100 text-blue-700' },
  exam_center: { label: 'Examination Center', icon: MapPin, color: 'bg-green-100 text-green-700' },
  institution: { label: 'Institution', icon: Building2, color: 'bg-purple-100 text-purple-700' },
  accreditation: { label: 'Accreditation', icon: Building2, color: 'bg-indigo-100 text-indigo-700' },
  curriculum: { label: 'Curriculum Provider', icon: BookOpen, color: 'bg-amber-100 text-amber-700' },
  admin: { label: 'Admin', icon: Shield, color: 'bg-red-100 text-red-700' },
};

export default function UserManagementTab({ token }) {
  const [activeSubTab, setActiveSubTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterRole, setFilterRole] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [showActivityDialog, setShowActivityDialog] = useState(false);
  const [showBulkImportDialog, setShowBulkImportDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [activityLogs, setActivityLogs] = useState([]);
  const [userActivity, setUserActivity] = useState(null);
  const [bulkImportData, setBulkImportData] = useState([]);
  const [bulkImportErrors, setBulkImportErrors] = useState([]);
  const fileInputRef = useRef(null);
  
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'examiner',
    phone: '',
    organization: '',
    designation: '',
    specializations: '',
    address: ''
  });
  
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState('');

  useEffect(() => {
    fetchUsers();
  }, [filterRole]);

  useEffect(() => {
    if (activeSubTab === 'activity') {
      fetchActivityLogs();
    }
  }, [activeSubTab]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = filterRole !== 'all' ? `?role=${filterRole}` : '';
      const res = await axios.get(`${API}/admin/managed-users${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchActivityLogs = async () => {
    try {
      const res = await axios.get(`${API}/admin/activity-logs?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActivityLogs(res.data.logs || []);
    } catch (err) {
      console.error('Failed to load activity logs');
    }
  };

  const fetchUserActivity = async (userId) => {
    try {
      const res = await axios.get(`${API}/admin/user-activity/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserActivity(res.data);
      setShowActivityDialog(true);
    } catch (err) {
      toast.error('Failed to load user activity');
    }
  };

  const handleCreateUser = async () => {
    try {
      const payload = {
        ...newUser,
        specializations: newUser.specializations ? newUser.specializations.split(',').map(s => s.trim()) : null
      };
      
      await axios.post(`${API}/admin/create-user`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`${ROLE_CONFIG[newUser.role]?.label || 'User'} created successfully`);
      setShowCreateDialog(false);
      setNewUser({
        email: '', password: '', full_name: '', role: 'examiner',
        phone: '', organization: '', designation: '', specializations: '', address: ''
      });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleStatusChange = async (userId, status) => {
    try {
      await axios.put(`${API}/admin/managed-users/${userId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`User ${status === 'active' ? 'activated' : 'deactivated'}`);
      fetchUsers();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    try {
      await axios.put(`${API}/admin/managed-users/${selectedUser.id}/reset-password?new_password=${encodeURIComponent(newPassword)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Password reset successfully');
      setShowResetDialog(false);
      setNewPassword('');
      setSelectedUser(null);
    } catch (err) {
      toast.error('Failed to reset password');
    }
  };

  const handleSendPasswordResetEmail = async (userId) => {
    try {
      const res = await axios.post(`${API}/admin/users/${userId}/send-password-reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
    } catch (err) {
      toast.error('Failed to send password reset email');
    }
  };

  const handleRoleChange = async () => {
    if (!newRole) {
      toast.error('Please select a role');
      return;
    }
    try {
      await axios.put(`${API}/admin/users/${selectedUser.id}/role?new_role=${newRole}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Role updated successfully');
      setShowRoleDialog(false);
      setNewRole('');
      setSelectedUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/managed-users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User deleted');
      fetchUsers();
    } catch (err) {
      toast.error('Failed to delete user');
    }
  };

  const handleExportUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/users/export?role=${filterRole}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `users_export_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Users exported successfully');
    } catch (err) {
      toast.error('Failed to export users');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split('\n');
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      
      const data = [];
      for (let i = 1; i < lines.length; i++) {
        if (!lines[i].trim()) continue;
        const values = lines[i].split(',');
        const row = {};
        headers.forEach((h, idx) => {
          row[h] = values[idx]?.trim() || '';
        });
        if (row.email) {
          data.push(row);
        }
      }
      
      setBulkImportData(data);
      setShowBulkImportDialog(true);
    };
    reader.readAsText(file);
  };

  const handleBulkImport = async () => {
    try {
      const res = await axios.post(`${API}/admin/users/bulk-create`, bulkImportData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Created ${res.data.created} users`);
      setBulkImportErrors(res.data.errors || []);
      if (res.data.errors?.length === 0) {
        setShowBulkImportDialog(false);
        setBulkImportData([]);
      }
      fetchUsers();
    } catch (err) {
      toast.error('Failed to import users');
    }
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  const filteredUsers = users.filter(u => 
    searchTerm === '' || 
    u.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.organization?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="user-management-tab">
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="users" className="data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white">
            <Users className="h-4 w-4 mr-2" /> Users
          </TabsTrigger>
          <TabsTrigger value="activity" className="data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white">
            <History className="h-4 w-4 mr-2" /> Activity Logs
          </TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users" className="mt-4">
          {/* Header with Actions */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <h2 className="text-xl font-semibold text-[#1B2A4A]">User Management</h2>
              <p className="text-sm text-slate-500">Create, manage, and monitor user accounts</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" className="rounded-sm" onClick={handleExportUsers}>
                <Download className="h-4 w-4 mr-2" /> Export CSV
              </Button>
              <Button variant="outline" className="rounded-sm" onClick={() => fileInputRef.current?.click()}>
                <Upload className="h-4 w-4 mr-2" /> Import CSV
              </Button>
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept=".csv"
                onChange={handleFileSelect}
              />
              <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-[#1B2A4A] text-white rounded-sm">
                    <UserPlus className="h-4 w-4 mr-2" /> Create User
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Create New User Account</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <Label>Role *</Label>
                        <Select value={newUser.role} onValueChange={v => setNewUser({...newUser, role: v})}>
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="candidate">Candidate</SelectItem>
                            <SelectItem value="examiner">Examiner</SelectItem>
                            <SelectItem value="exam_center">Examination Center</SelectItem>
                            <SelectItem value="institution">Institution</SelectItem>
                            <SelectItem value="curriculum">Curriculum Provider</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Label>Full Name *</Label>
                        <Input 
                          value={newUser.full_name}
                          onChange={e => setNewUser({...newUser, full_name: e.target.value})}
                          className="mt-1"
                          placeholder="Dr. John Smith"
                        />
                      </div>
                      <div className="col-span-2">
                        <Label>Email *</Label>
                        <Input 
                          type="email"
                          value={newUser.email}
                          onChange={e => setNewUser({...newUser, email: e.target.value})}
                          className="mt-1"
                          placeholder="user@example.com"
                        />
                      </div>
                      <div className="col-span-2">
                        <Label>Password *</Label>
                        <div className="flex gap-2 mt-1">
                          <div className="relative flex-1">
                            <Input 
                              type={showPassword ? "text" : "password"}
                              value={newUser.password}
                              onChange={e => setNewUser({...newUser, password: e.target.value})}
                              placeholder="Min 8 characters"
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400"
                            >
                              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setNewUser({...newUser, password: generatePassword()})}
                          >
                            Generate
                          </Button>
                        </div>
                      </div>
                      <div>
                        <Label>Phone</Label>
                        <Input 
                          value={newUser.phone}
                          onChange={e => setNewUser({...newUser, phone: e.target.value})}
                          className="mt-1"
                          placeholder="+91 98765 43210"
                        />
                      </div>
                      <div>
                        <Label>Organization</Label>
                        <Input 
                          value={newUser.organization}
                          onChange={e => setNewUser({...newUser, organization: e.target.value})}
                          className="mt-1"
                          placeholder="Hospital/Clinic Name"
                        />
                      </div>
                      <div>
                        <Label>Designation</Label>
                        <Input 
                          value={newUser.designation}
                          onChange={e => setNewUser({...newUser, designation: e.target.value})}
                          className="mt-1"
                          placeholder="Senior Consultant"
                        />
                      </div>
                      <div>
                        <Label>Specializations</Label>
                        <Input 
                          value={newUser.specializations}
                          onChange={e => setNewUser({...newUser, specializations: e.target.value})}
                          className="mt-1"
                          placeholder="Dermatology, Aesthetics"
                        />
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
                    <Button 
                      onClick={handleCreateUser}
                      disabled={!newUser.email || !newUser.password || !newUser.full_name}
                      className="bg-[#1B2A4A]"
                    >
                      Create User
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Filters */}
          <div className="flex gap-4 mb-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input 
                  placeholder="Search by name, email, or organization..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={filterRole} onValueChange={setFilterRole}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="candidate">Candidates</SelectItem>
                <SelectItem value="examiner">Examiners</SelectItem>
                <SelectItem value="exam_center">Exam Centers</SelectItem>
                <SelectItem value="institution">Institutions</SelectItem>
                <SelectItem value="curriculum">Curriculum</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchUsers}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>

          {/* Users Table */}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>User</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Organization</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8">
                        <RefreshCw className="h-6 w-6 animate-spin mx-auto text-slate-400" />
                      </TableCell>
                    </TableRow>
                  ) : filteredUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                        No users found
                      </TableCell>
                    </TableRow>
                  ) : filteredUsers.map(u => {
                    const roleConfig = ROLE_CONFIG[u.role] || ROLE_CONFIG.candidate;
                    const RoleIcon = roleConfig.icon;
                    return (
                      <TableRow key={u.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium text-[#1B2A4A]">{u.full_name || 'N/A'}</p>
                            <p className="text-xs text-slate-500">{u.email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${roleConfig.color} text-xs`}>
                            <RoleIcon className="h-3 w-3 mr-1" />
                            {roleConfig.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">
                          {u.organization || '-'}
                        </TableCell>
                        <TableCell>
                          <Badge className={u.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                            {u.status === 'active' ? <CheckCircle className="h-3 w-3 mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
                            {u.status || 'active'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0"
                              title="View Activity"
                              onClick={() => fetchUserActivity(u.id)}
                            >
                              <History className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0"
                              title="Change Role"
                              onClick={() => { setSelectedUser(u); setNewRole(u.role); setShowRoleDialog(true); }}
                            >
                              <Shield className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0"
                              title="Reset Password"
                              onClick={() => { setSelectedUser(u); setShowResetDialog(true); }}
                            >
                              <Key className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0"
                              title="Send Password Reset Email"
                              onClick={() => handleSendPasswordResetEmail(u.id)}
                            >
                              <Mail className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className={`h-8 w-8 p-0 ${u.status === 'active' ? 'text-red-600' : 'text-green-600'}`}
                              title={u.status === 'active' ? 'Deactivate' : 'Activate'}
                              onClick={() => handleStatusChange(u.id, u.status === 'active' ? 'inactive' : 'active')}
                            >
                              {u.status === 'active' ? <XCircle className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0 text-red-600"
                              title="Delete User"
                              onClick={() => handleDeleteUser(u.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          <p className="text-sm text-slate-500 mt-2">
            Showing {filteredUsers.length} of {users.length} users
          </p>
        </TabsContent>

        {/* Activity Logs Tab */}
        <TabsContent value="activity" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <History className="h-4 w-4" /> Admin Activity Logs
              </CardTitle>
              <p className="text-sm text-slate-500">Audit trail of all admin actions</p>
            </CardHeader>
            <CardContent>
              {activityLogs.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <History className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                  <p>No activity logs found</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {activityLogs.map(log => (
                    <div key={log.id} className="p-4 border rounded-sm hover:bg-slate-50">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-[#1B2A4A]">{log.description}</p>
                          <p className="text-xs text-slate-500 mt-1">
                            Action: <Badge variant="outline" className="ml-1">{log.action}</Badge>
                          </p>
                          <p className="text-xs text-slate-400 mt-1">
                            By: {log.admin_name} ({log.admin_email})
                          </p>
                        </div>
                        <p className="text-xs text-slate-400">
                          {log.timestamp ? new Date(log.timestamp).toLocaleString() : '-'}
                        </p>
                      </div>
                      {log.details && Object.keys(log.details).length > 0 && (
                        <div className="mt-2 p-2 bg-slate-50 rounded text-xs font-mono">
                          {JSON.stringify(log.details, null, 2)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Reset Password Dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600 mb-4">
              Reset password for: <strong>{selectedUser?.full_name}</strong> ({selectedUser?.email})
            </p>
            <Label>New Password</Label>
            <div className="flex gap-2 mt-1">
              <Input 
                type={showPassword ? "text" : "password"}
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="Min 8 characters"
              />
              <Button variant="outline" onClick={() => setNewPassword(generatePassword())}>Generate</Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowResetDialog(false); setNewPassword(''); }}>Cancel</Button>
            <Button onClick={handleResetPassword} className="bg-[#1B2A4A]">Reset Password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Role Dialog */}
      <Dialog open={showRoleDialog} onOpenChange={setShowRoleDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change User Role</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600 mb-4">
              Change role for: <strong>{selectedUser?.full_name}</strong>
            </p>
            <Label>New Role</Label>
            <Select value={newRole} onValueChange={setNewRole}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="candidate">Candidate</SelectItem>
                <SelectItem value="examiner">Examiner</SelectItem>
                <SelectItem value="exam_center">Examination Center</SelectItem>
                <SelectItem value="institution">Institution</SelectItem>
                <SelectItem value="accreditation">Accreditation</SelectItem>
                <SelectItem value="curriculum">Curriculum Provider</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRoleDialog(false)}>Cancel</Button>
            <Button onClick={handleRoleChange} className="bg-[#1B2A4A]">Update Role</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Activity Dialog */}
      <Dialog open={showActivityDialog} onOpenChange={setShowActivityDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>User Activity: {userActivity?.user?.full_name}</DialogTitle>
          </DialogHeader>
          {userActivity && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-slate-50 rounded-sm">
                <p className="text-sm"><strong>Email:</strong> {userActivity.user.email}</p>
                <p className="text-sm"><strong>Role:</strong> {userActivity.user.role}</p>
                <p className="text-sm"><strong>Status:</strong> {userActivity.user.status}</p>
                <p className="text-sm"><strong>Created:</strong> {userActivity.user.created_at ? new Date(userActivity.user.created_at).toLocaleDateString() : '-'}</p>
              </div>
              
              <div>
                <h4 className="font-medium mb-2">Recent Logins</h4>
                {userActivity.login_history?.length > 0 ? (
                  <div className="space-y-1">
                    {userActivity.login_history.slice(0, 10).map((login, idx) => (
                      <p key={idx} className="text-sm text-slate-600">
                        {new Date(login.timestamp).toLocaleString()} - {login.ip_address || 'Unknown IP'}
                      </p>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">No login history</p>
                )}
              </div>

              <div>
                <h4 className="font-medium mb-2">Applications</h4>
                {userActivity.applications?.length > 0 ? (
                  <div className="space-y-1">
                    {userActivity.applications.map((app, idx) => (
                      <p key={idx} className="text-sm text-slate-600">
                        {app.type} - {app.status} ({app.created_at ? new Date(app.created_at).toLocaleDateString() : '-'})
                      </p>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">No applications</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Import Dialog */}
      <Dialog open={showBulkImportDialog} onOpenChange={setShowBulkImportDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Bulk Import Users</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600 mb-4">
              {bulkImportData.length} users found in CSV. Review and import:
            </p>
            
            {bulkImportErrors.length > 0 && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
                <p className="font-medium text-red-700 mb-2">Errors:</p>
                {bulkImportErrors.map((err, idx) => (
                  <p key={idx} className="text-sm text-red-600">Row {err.row}: {err.error}</p>
                ))}
              </div>
            )}

            <div className="max-h-64 overflow-y-auto border rounded">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Organization</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bulkImportData.slice(0, 50).map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="text-sm">{row.email}</TableCell>
                      <TableCell className="text-sm">{row.full_name}</TableCell>
                      <TableCell className="text-sm">{row.role || 'candidate'}</TableCell>
                      <TableCell className="text-sm">{row.organization || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {bulkImportData.length > 50 && (
              <p className="text-xs text-slate-400 mt-2">Showing first 50 of {bulkImportData.length} users</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowBulkImportDialog(false); setBulkImportData([]); setBulkImportErrors([]); }}>
              Cancel
            </Button>
            <Button onClick={handleBulkImport} className="bg-[#1B2A4A]">
              Import {bulkImportData.length} Users
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
